import { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  makeStyles,
  Step,
  StepLabel,
  Stepper,
} from '@material-ui/core';
import { StepIconProps } from '@material-ui/core/StepIcon';
import { CheckRounded as CheckIcon } from '@material-ui/icons';

import { ORGANIZATION_TYPE } from '../../types/organization.type';
import { useCreateOrganization } from '../../hooks/create-organization.hook';
import { CreateOrganizationFormValues } from '../Forms/create-organization-form.type';
import { InformationsStep } from '../Forms/InformationsStep.component';
import { ContactStep } from '../Forms/ContactStep.component';
import { MembersStep } from '../Forms/MembersStep.component';
import { DeliveryPointsStep } from '../Forms/DeliveryPointsStep.component';
import { DISCOUNT_TYPES } from '../../../shared/types/discount.type';
import { mapCreateOrganizationFormValuesToInput } from '../../utils/map-create-organization-form-values.util';

type Props = {
  onCreated: (organizationId: string) => void;
};

const STEPS = ['Informations', 'Contact', 'Membres', 'Points de livraison'];

const DEFAULT_VALUES: CreateOrganizationFormValues = {
  type: ORGANIZATION_TYPE.CONCIERGERY,
  name: '',
  description: '',
  address: { street: '', zipCode: '', city: '', informations: '' },
  discount: 0,
  reduction: DISCOUNT_TYPES.PERCENT,
  isRestricted: true,
  contactMode: 'existing',
  selectedContact: null,
  newContact: { firstname: '', lastname: '', email: '', phoneNumber: '' },
  selectedMembers: [],
  selectedDeliveryPoints: [],
};

const STEP_FIELDS: Record<number, (keyof CreateOrganizationFormValues)[]> = {
  0: ['type', 'name', 'address', 'discount', 'reduction', 'isRestricted'],
};

const useStyles = makeStyles((theme) => ({
  triggerButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
  dialog: {
    '& > .MuiDialog-container > .MuiPaper-root': {
      minWidth: '65svw',
      maxWidth: '65svw',
    },
  },
  stepper: {
    width: '80%',
    alignSelf: 'center',
    padding: '1rem 1.5rem',
  },
  stepIcon: (props: { active: boolean; completed: boolean }) => ({
    width: 28,
    height: 28,
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: 14,
    backgroundColor: props.active || props.completed ? theme.palette.admin.primary.main : '#e0e0e0',
    color: props.active || props.completed ? theme.palette.admin.primary.contrastText : '#757575',
  }),
  stepIconCheck: {
    fontSize: 18,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '0.5rem 1.5rem 1.5rem',
  },
  actions: {
    padding: '0.75rem 1.5rem',
  },
  primaryButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.admin.primary.light,
      color: theme.palette.admin.primary.contrastText,
    },
  },
}));

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isContactStepValid(data: CreateOrganizationFormValues): boolean {
  if (data.contactMode === 'existing') {
    return data.selectedContact !== null;
  }

  const { firstname, lastname, email, phoneNumber } = data.newContact;
  return (
    firstname.trim().length > 0 &&
    lastname.trim().length > 0 &&
    EMAIL_REGEX.test(email.trim()) &&
    phoneNumber.trim().length === 10
  );
}

function OrganizationStepIcon(props: StepIconProps) {
  const { active = false, completed = false, icon } = props;
  const classes = useStyles({ active, completed });
  return <div className={classes.stepIcon}>{completed ? <CheckIcon className={classes.stepIconCheck} /> : icon}</div>;
}

export default function CreateOrganizationDialog({ onCreated }: Props) {
  const classes = useStyles({ active: false, completed: false });

  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const { createOrganization, isCreating } = useCreateOrganization();

  const { control, handleSubmit, reset, trigger, watch, setValue, getValues } = useForm<CreateOrganizationFormValues>({
    defaultValues: DEFAULT_VALUES,
    mode: 'onChange',
  });

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    if (isCreating) return;
    setOpen(false);
    reset();
    setActiveStep(0);
  };

  const handleNext = async () => {
    const fields = STEP_FIELDS[activeStep];
    if (fields) {
      const isValid = await trigger(fields);
      if (!isValid) return;
    }

    if (activeStep === 1) {
      const isContactValid = isContactStepValid(getValues());
      if (!isContactValid) {
        await trigger(['newContact']);
        return;
      }
    }

    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const onSubmit = async (data: CreateOrganizationFormValues) => {
    if (!isContactStepValid(data)) return;
    const input = mapCreateOrganizationFormValuesToInput(data);
    const created = await createOrganization(input);
    if (created) {
      handleClose();
      onCreated(created.id);
    }
  };

  const isLastStep = activeStep === STEPS.length - 1;

  return (
    <>
      <Button className={classes.triggerButton} variant="contained" size="small" onClick={handleOpen}>
        Cr&eacute;er une organisation
      </Button>
      <Dialog open={open} onClose={handleClose} className={classes.dialog}>
        <Stepper activeStep={activeStep} className={classes.stepper}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel StepIconComponent={OrganizationStepIcon}>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <DialogContent className={classes.content}>
          {activeStep === 0 && <InformationsStep control={control} />}
          {activeStep === 1 && <ContactStep control={control} watch={watch} setValue={setValue} />}
          {activeStep === 2 && <MembersStep watch={watch} setValue={setValue} />}
          {activeStep === 3 && <DeliveryPointsStep watch={watch} setValue={setValue} />}
        </DialogContent>
        <DialogActions className={classes.actions}>
          <Button onClick={handleClose} disabled={isCreating}>
            Annuler
          </Button>
          <section>
            {activeStep > 0 && (
              <Button onClick={handleBack} disabled={isCreating}>
                Retour
              </Button>
            )}
            {!isLastStep && (
              <Button variant="contained" className={classes.primaryButton} onClick={handleNext}>
                Suivant
              </Button>
            )}
            {isLastStep && (
              <Button
                variant="contained"
                className={classes.primaryButton}
                onClick={handleSubmit(onSubmit)}
                disabled={isCreating}
                startIcon={isCreating ? <CircularProgress size={16} /> : null}
              >
                {isCreating ? 'Création...' : 'Créer'}
              </Button>
            )}
          </section>
        </DialogActions>
      </Dialog>
    </>
  );
}
